// The gate on completing a job: every quality check resolved.
//
// Kept out of the queries module so it can be pinned by a test without the
// React Query graph behind it — the rule is a pure function of the checks.

import type { CheckVM } from '@/api/types';

export interface ChecksProgress {
  /** Checks the operator is finished with: passed or marked N/A. */
  done: number;
  total: number;
  /** Checks that failed — each holds the sign-off until it is passed. */
  failed: number;
  /** Odoo will accept button_finish: nothing open, nothing failed. */
  ready: boolean;
}

/**
 * Every check passed or N/A — the gate on completing the job.
 *
 * Resolved means 'pass' or 'na': interpod_quality's override of
 * `verify_quality_checks` exempts 'na' the way native exempts 'pass', and the
 * same override refuses `button_finish` while any check is 'fail' (naming the
 * failed checks in a UserError). So a failed check is not done — it is the
 * reason the Done button is blocked until the operator rectifies and presses
 * Pass on it (`do_pass_immediate` overwrites a fail).
 */
export function checksResolved(checks: CheckVM[] | undefined): ChecksProgress {
  const list = checks ?? [];
  const done = list.filter((c) => c.state === 'pass' || c.state === 'na').length;
  const failed = list.filter((c) => c.state === 'fail').length;
  // A step with no quality points is completable. Plenty of work centres have
  // none configured, and Odoo's own verify_quality_checks passes when there is
  // nothing to verify — refusing here would strand the operator.
  return { done, total: list.length, failed, ready: done === list.length };
}
